// Local (no-AI) insights engine.
//
// Produces the SAME structured object shape as generateStructuredInsight() so it
// renders through the unchanged UnifiedDashboard. It requires no LLM key — every
// value is derived from the canonical + analytical metrics TimechainStats already
// computes client-side.
//
// Voice: mirrors the AI harness's Bitcoin-native framing (STRUCTURED_SYSTEM_PROMPT
// in aiService.js). High hashrate/difficulty = security (good). Low fees = cheap,
// good for adoption. Growing Lightning = scaling. Never "dominance", never
// "growing scarcity" (21M is fixed), never claim ATH, never blame short-term price
// on supply mechanics. Terminology: timechain, sats, HODL, proof of work, subsidy.
//
// Output pattern (mirrors the LLM, not a fixed list): candidate insights/outlook
// carry a notability SCORE (anomaly > benchmark extreme > strong trend > imminent
// difficulty adjustment > MA/percentile/volatility signals > baseline facts). We
// rank by score and take the top N — so the dashboard LEADS WITH WHAT'S INTERESTING
// and SKIPS THE BORING, instead of always emitting the same rows. An hourly focus
// rotation (mirroring FOCUS_ANGLES in aiService.js) shifts the lens across the day,
// and cross-metric connections mirror the LLM's "connect two metrics" instruction.

import { getCanonicalMetrics, getMetricsSummary } from './aiService';
import { computeAnalyticalMetrics } from '../data/analyticalMetrics';
import { fmt, getAILocalFacts } from '../data/canonicalMetrics';

const ALL_CATEGORIES = ['market', 'network', 'mining', 'lightning', 'mempool'];

// Per-pull rotation counter: increments once per generateLocalInsight call (the button
// calls it exactly once per generation) so the same underlying data reads differently
// each pull — mirroring the LLM's varied output — without ever hiding notable signals.
let pullCounter = 0;

const lc = (s) => (s || '').toLowerCase();

// ---- Word-limit helpers (mirror the AI schema: headline 11, analysis 15, watch 10) ----
const capWords = (text, max) => {
  if (!text) return text;
  const words = text.trim().split(/\s+/);
  if (words.length <= max) return text.trim();
  return words.slice(0, max).join(' ').replace(/[,;:—-]$/, '') + '…';
};

const feeRate = (n) => (n != null ? `${n} sat/vB` : null);
const btcSubsidy = (sats) => (sats != null ? (sats / 1e8).toFixed(3) : '3.125');

// Join a lead phrase with an optional benchmark context as two clean sentences.
// If the context is missing/empty, we return just the lead — never a dangling separator.
const withContext = (lead, ctx) => {
  const c = (ctx || '').trim().replace(/[.\s]+$/, '');
  if (!c) return `${lead}.`;
  return `${lead}. ${c.charAt(0).toUpperCase()}${c.slice(1)}.`;
};

// Map an analytical benchmark level to an AI-schema section status
const mempoolStatus = (level) => {
  switch (level) {
    case 'LIGHT':
    case 'NORMAL': return 'clear';
    case 'BUSY': return 'busy';
    case 'CONGESTED': return 'congested';
    default: return 'clear';
  }
};

/**
 * Generate a full insight dashboard from data alone (no external LLM).
 * @param {object} data - the same combined data object passed to generateStructuredInsight
 * @param {string[]|null} selectedCategories - subset of ALL_CATEGORIES, or null for all
 * @returns {object} same shape as generateStructuredInsight's result
 */
export const generateLocalInsight = (data, selectedCategories = null) => {
  const cats = Array.isArray(selectedCategories) && selectedCategories.length > 0
    ? selectedCategories
    : ALL_CATEGORIES;
  const wants = (c) => cats.includes(c);

  // ---- Per-run variety seed (mirrors the LLM's varied output per analysis run) ----
  pullCounter = (pullCounter + 1) % 1000000;
  const now = new Date();
  const seed = pullCounter + now.getHours() * 5 + now.getMinutes();
  const keyHash = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
  };
  // Choose one phrasing from equivalent variants, offset per key so lines rotate out of phase.
  const pick = (key, variants) => variants[(seed + keyHash(key)) % variants.length];
  // Small per-key jitter so near-tied BASELINE candidates rotate which ones surface each run,
  // while genuinely notable candidates (scored far higher) still lead.
  const jitter = (key) => ((seed * 7 + keyHash(key)) % 9) - 4;

  const canonical = getCanonicalMetrics(data);
  const m = getMetricsSummary(data);
  const analyticalMetrics = computeAnalyticalMetrics(canonical, data);
  const an = analyticalMetrics?.analytics || {};
  const b = an.benchmarks || {};
  const trend = an.trends?.price || {};
  const flags = an.flags || {};
  const sentiment = an.marketCondition?.sentiment || 'NEUTRAL';

  const raw = canonical?.raw || {};
  const price = raw.price;

  // ---- Derived analytical signals (the same well the LLM prompt draws from) ----
  const hrTrend = an.trends?.hashrate || {};
  const lnTrend = an.trends?.lightning || {};
  const ma = an.trends?.movingAverages?.price || {};
  const vol = an.trends?.priceVolatility;
  const volLevel = flags.volatilityLevel;
  const pctl = an.trends?.pricePercentile;
  const corr = an.correlations?.priceHashrate;
  const daProg = m?.progressBars?.difficulty?.progress;
  const athPct = m?.price?.athDistance; // negative = below ATH; never claim ATH
  const isStrong = (t) => t.direction === 'BULLISH' || t.direction === 'BEARISH';

  // ---- Hourly focus rotation — mirrors FOCUS_ANGLES in aiService.js. ----
  // Identical data still reads differently across the day by boosting the hour's lens.
  const FOCUS_ROTATION = [
    ['mining'],               // miner economics & fee/subsidy
    ['lightning', 'market'],  // adoption signals
    ['market'],               // market structure
    ['network'],              // network security
    ['mempool', 'mining'],    // fee market
    ['mining'],               // supply narrative
  ];
  const focusCats = FOCUS_ROTATION[now.getHours() % FOCUS_ROTATION.length];
  const focusBoost = (cat) => (focusCats.includes(cat) ? 12 : 0);

  // Rank scored candidates: dedupe by key (keep highest), filter to wanted cats,
  // sort by focus-boosted score desc, format + word-cap, take n. This is what makes
  // the engine lead with anomalies/extremes instead of a fixed list.
  const rank = (pool, cap, n) => {
    const seen = new Map();
    pool.filter(Boolean).forEach((c) => {
      const s = (c.score || 0) + focusBoost(c.cat) + jitter(c.key);
      const prev = seen.get(c.key);
      if (!prev || s > prev._s) seen.set(c.key, { ...c, _s: s });
    });
    return [...seen.values()]
      .filter((c) => wants(c.cat))
      .sort((a, b2) => b2._s - a._s)
      .map((c) => capWords(c.text, cap))
      .slice(0, n);
  };

  // ---- Sections (status only — UnifiedDashboard overrides highlights from real metrics) ----
  const sectionDefs = {
    market: {
      status: sentiment === 'BULLISH' ? 'strong' : sentiment === 'BEARISH' ? 'weak' : 'neutral',
    },
    network: {
      status: flags.networkHealth === 'HEALTHY' ? 'healthy'
        : flags.networkHealth === 'STRESSED' ? 'stressed' : 'neutral',
    },
    mining: {
      status: b.hashrate?.level === 'LOW' ? 'challenging' : 'profitable',
    },
    lightning: {
      // Growing when capacity is expanding; otherwise stable. No decline signal without trend data.
      status: (lnTrend.direction === 'BULLISH' || m?.lightning?.channelChange > 0 || m?.lightning?.nodeChange > 0)
        ? 'growing' : 'stable',
    },
    mempool: {
      status: mempoolStatus(b.mempool?.level),
    },
  };
  const sections = {};
  cats.forEach((c) => { if (sectionDefs[c]) sections[c] = sectionDefs[c]; });

  // ---- Market score: overall NETWORK HEALTH, security + accessibility + trend ----
  let score = 5;
  if (b.hashrate?.level === 'HIGH') score += 2;
  else if (b.hashrate?.level === 'NORMAL') score += 1;
  else if (b.hashrate?.level === 'LOW') score -= 2;
  if (b.fees?.level === 'LOW') score += 1;
  else if (b.fees?.level === 'NORMAL') score += 0.5;
  else if (b.fees?.level === 'HIGH') score -= 1;
  else if (b.fees?.level === 'EXTREME') score -= 1.5;
  if (b.mempool?.level === 'CONGESTED') score -= 1;
  else if (b.mempool?.level === 'BUSY') score -= 0.5;
  else score += 0.5;
  if (b.blockTime?.level === 'SLOW') score -= 0.5;
  else if (b.blockTime?.level === 'FAST' || b.blockTime?.level === 'ON_TARGET') score += 0.5;
  if (trend.direction === 'BULLISH') score += 1;
  else if (trend.direction === 'BEARISH') score -= 1;
  const marketScore = Math.max(1, Math.min(10, Math.round(score)));

  // ---- Headline: price + the single most notable observation ----
  const priceStr = price != null ? `$${Math.round(price).toLocaleString()}` : '$—';
  const headlineObs = () => {
    const a = (an.anomalies || [])[0];
    if (a) {
      if (a.metric === 'hashrate') return pick('h-hr', ['as hashrate slips from recent levels', 'as hashrate eases off recent highs']);
      if (a.metric === 'volatility') return pick('h-vol', ['through a burst of volatility', 'amid a volatility spike']);
      if (a.metric === 'avgBlockTime') return a.direction === 'SLOW'
        ? pick('h-bts', ['as blocks come in slow', 'with blocks running slow'])
        : pick('h-btf', ['as blocks come in fast', 'with blocks running fast']);
    }
    if (b.mempool?.level === 'CONGESTED') return pick('h-mp', ['as the mempool stays congested', 'while the mempool runs congested']);
    if (b.fees?.level === 'EXTREME' || b.fees?.level === 'HIGH') return pick('h-fee', ['as fees climb', 'with fees running hot']);
    if (b.fees?.level === 'LOW') return pick('h-feelo', ['while fees stay low', 'with block space cheap']);
    if (volLevel === 'HIGH') return pick('h-volh', ['amid elevated volatility', 'through choppy price action']);
    if (trend.direction === 'BULLISH' && trend.strength === 'STRONG') return pick('h-bull', ['as momentum builds', 'with momentum to the upside']);
    if (trend.direction === 'BEARISH') return pick('h-bear', ['under short-term pressure', 'amid near-term selling']);
    if (athPct != null && athPct > -5) return pick('h-ath', ['near its all-time high', 'close to record territory']);
    if (b.hashrate?.level === 'HIGH') return pick('h-hrh', ['with hashrate holding strong', 'as hashrate holds firm']);
    if (b.hashrate?.level === 'LOW') return pick('h-hrl', ['as hashrate softens', 'with hashrate easing']);
    return pick('h-steady', ['on a steady network', 'across a steady timechain', 'with the network humming along']);
  };
  const headlineStem = pick('h-stem', [
    `Bitcoin trades at ${priceStr}`,
    `BTC changes hands at ${priceStr}`,
    `Bitcoin at ${priceStr}`,
  ]);
  const headline = capWords(`${headlineStem} ${headlineObs()}`, 11);

  // ---- Analysis (insights): scored candidates, ranked, each ≤15 words ----
  const insightPool = [
    // Network / security
    b.hashrate && raw.hashrate != null && {
      key: 'hashrate', cat: 'network',
      score: b.hashrate.level === 'LOW' ? 85 : b.hashrate.level === 'HIGH' ? 55 : 28,
      text: withContext(pick('hashrate', [
        `Hashrate at ${fmt.hashrate(raw.hashrate)}`,
        `Network hashrate ${fmt.hashrate(raw.hashrate)}`,
        `Miners securing the timechain at ${fmt.hashrate(raw.hashrate)}`,
      ]), b.hashrate.context),
    },
    isStrong(hrTrend) && {
      key: 'hashrate-trend', cat: 'network',
      score: hrTrend.strength === 'STRONG' ? 70 : 48,
      text: pick('hashrate-trend', [
        `Hashrate trending ${lc(hrTrend.direction)} over recent weeks, ${lc(hrTrend.strength)} momentum.`,
        `Proof-of-work ${lc(hrTrend.direction)} across recent weeks, ${lc(hrTrend.strength)} pace.`,
        `Miner hashrate ${lc(hrTrend.direction)} lately, ${lc(hrTrend.strength)} momentum.`,
      ]),
    },
    b.blockTime && m?.network?.avgBlockTime != null && {
      key: 'blocktime', cat: 'network',
      score: b.blockTime.level === 'SLOW' ? 78 : b.blockTime.level === 'FAST' ? 70 : 22,
      text: withContext(pick('blocktime', [
        `Blocks averaging ${fmt.time(m.network.avgBlockTime)}`,
        `Block interval near ${fmt.time(m.network.avgBlockTime)}`,
        `Blocks arriving every ${fmt.time(m.network.avgBlockTime)}`,
      ]), b.blockTime.context),
    },
    daProg != null && {
      key: 'difficulty', cat: 'network',
      score: daProg >= 90 ? 68 : daProg >= 75 ? 50 : 24,
      text: pick('difficulty', [
        `Difficulty ${fmt.pct(daProg)} through the epoch${daProg >= 90 ? ', adjustment imminent' : ''}.`,
        `Current epoch ${fmt.pct(daProg)} complete${daProg >= 90 ? ', retarget imminent' : ''}.`,
        `${fmt.pct(daProg)} into the difficulty epoch${daProg >= 90 ? ', adjustment near' : ''}.`,
      ]),
    },
    // Mining
    b.fees && raw.fastestFee != null && {
      key: 'fees', cat: 'mining',
      score: b.fees.level === 'EXTREME' ? 88 : b.fees.level === 'HIGH' ? 80
        : b.fees.level === 'ELEVATED' ? 55 : b.fees.level === 'LOW' ? 40 : 26,
      text: withContext(pick('fees', [
        `Fastest fee ${feeRate(raw.fastestFee)}`,
        `Priority fee ${feeRate(raw.fastestFee)}`,
        `Next-block fee ${feeRate(raw.fastestFee)}`,
      ]), b.fees.context),
    },
    m?.mining?.feeSubsidyRatio != null && {
      key: 'fee-subsidy', cat: 'mining', score: 38,
      text: pick('fee-subsidy', [
        `Fees add ${fmt.pct(m.mining.feeSubsidyRatio, 2)} atop the ${btcSubsidy(m.mining.currentReward)} BTC subsidy.`,
        `Fees top up the ${btcSubsidy(m.mining.currentReward)} BTC subsidy by ${fmt.pct(m.mining.feeSubsidyRatio, 2)}.`,
        `Miners earn ${fmt.pct(m.mining.feeSubsidyRatio, 2)} in fees over the ${btcSubsidy(m.mining.currentReward)} BTC subsidy.`,
      ]),
    },
    m?.mining?.halvingProgress != null && {
      key: 'halving', cat: 'mining', score: 26,
      text: pick('halving', [
        `${fmt.pct(m.mining.halvingProgress)} through Epoch ${m.mining.currentEpoch || 5}, ${btcSubsidy(m.mining.currentReward)} BTC subsidy.`,
        `Epoch ${m.mining.currentEpoch || 5} is ${fmt.pct(m.mining.halvingProgress)} done, subsidy ${btcSubsidy(m.mining.currentReward)} BTC.`,
        `${fmt.pct(m.mining.halvingProgress)} toward the next halving, ${btcSubsidy(m.mining.currentReward)} BTC per block.`,
      ]),
    },
    // Market
    m?.price?.change30d != null && {
      key: 'price-30d', cat: 'market',
      score: Math.abs(m.price.change30d) > 15 ? 58 : Math.abs(m.price.change30d) > 5 ? 36 : 22,
      text: pick('price-30d', [
        `Price ${m.price.change30d >= 0 ? 'up' : 'down'} ${fmt.pct(Math.abs(m.price.change30d))} over 30 days.`,
        `BTC ${m.price.change30d >= 0 ? 'gained' : 'shed'} ${fmt.pct(Math.abs(m.price.change30d))} across the past month.`,
        `Last 30 days: price ${m.price.change30d >= 0 ? 'up' : 'down'} ${fmt.pct(Math.abs(m.price.change30d))}.`,
      ]),
    },
    ma.currentVsMa30 != null && Math.abs(ma.currentVsMa30) > 3 && {
      key: 'price-ma', cat: 'market',
      score: Math.abs(ma.currentVsMa30) > 10 ? 60 : 42,
      text: pick('price-ma', [
        `Price ${ma.currentVsMa30 >= 0 ? 'above' : 'below'} its 30-day average by ${fmt.pct(Math.abs(ma.currentVsMa30))}.`,
        `Trading ${fmt.pct(Math.abs(ma.currentVsMa30))} ${ma.currentVsMa30 >= 0 ? 'over' : 'under'} the 30-day mean.`,
      ]),
    },
    isStrong(trend) && {
      key: 'price-trend', cat: 'market',
      score: trend.strength === 'STRONG' ? 70 : 46,
      text: pick('price-trend', [
        `Price trend ${lc(trend.direction)}, ${lc(trend.strength)} on recent averages.`,
        `Recent averages read ${lc(trend.direction)}, ${lc(trend.strength)} strength.`,
      ]),
    },
    (volLevel === 'HIGH' || volLevel === 'ELEVATED') && vol != null && {
      key: 'volatility', cat: 'market',
      score: volLevel === 'HIGH' ? 64 : 44,
      text: pick('volatility', [
        `30-day volatility at ${fmt.pct(vol)}, ${lc(volLevel)} by recent standards.`,
        `Volatility running ${fmt.pct(vol)} over 30 days, ${lc(volLevel)}.`,
      ]),
    },
    pctl != null && (pctl >= 80 || pctl <= 20) && {
      key: 'percentile', cat: 'market', score: 48,
      text: pick('percentile', [
        `Price in the ${pctl}th percentile of its 90-day range.`,
        `Sitting at the ${pctl}th percentile of the last 90 days.`,
      ]),
    },
    athPct != null && {
      key: 'ath', cat: 'market',
      score: athPct > -5 ? 52 : athPct < -30 ? 40 : 24,
      text: pick('ath', [
        `Trading ${fmt.pct(Math.abs(athPct))} below the all-time high.`,
        `Still ${fmt.pct(Math.abs(athPct))} under the prior all-time high.`,
      ]),
    },
    m?.activity?.transactions24h != null && {
      key: 'txs', cat: 'market', score: 24,
      text: pick('txs', [
        `${fmt.num(m.activity.transactions24h)} transactions settled on-chain in 24 hours.`,
        `${fmt.num(m.activity.transactions24h)} on-chain transactions cleared over the past day.`,
      ]),
    },
    // Mempool
    b.mempool && m?.activity?.mempoolTxCount != null && {
      key: 'mempool', cat: 'mempool',
      score: b.mempool.level === 'CONGESTED' ? 85 : b.mempool.level === 'BUSY' ? 55 : 26,
      text: withContext(pick('mempool', [
        `Mempool holds ${fmt.num(m.activity.mempoolTxCount)} unconfirmed txs`,
        `${fmt.num(m.activity.mempoolTxCount)} transactions waiting in the mempool`,
        `Mempool backlog of ${fmt.num(m.activity.mempoolTxCount)} unconfirmed txs`,
      ]), b.mempool.context),
    },
    // Lightning
    m?.lightning?.capacity != null && m?.lightning?.nodes != null && {
      key: 'lightning', cat: 'lightning', score: 30,
      text: pick('lightning', [
        `Lightning holds ${fmt.btcShort(m.lightning.capacity)} BTC across ${fmt.num(m.lightning.nodes)} nodes.`,
        `${fmt.num(m.lightning.nodes)} Lightning nodes carry ${fmt.btcShort(m.lightning.capacity)} BTC of capacity.`,
        `Layer 2: ${fmt.btcShort(m.lightning.capacity)} BTC across ${fmt.num(m.lightning.nodes)} Lightning nodes.`,
      ]),
    },
    isStrong(lnTrend) && {
      key: 'lightning-trend', cat: 'lightning',
      score: lnTrend.strength === 'STRONG' ? 58 : 44,
      text: pick('lightning-trend', [
        `Lightning capacity ${lc(lnTrend.direction)} over two weeks, ${lc(lnTrend.strength)}.`,
        `L2 capacity ${lc(lnTrend.direction)} across two weeks, ${lc(lnTrend.strength)} pace.`,
      ]),
    },
    // ---- Cross-metric connections (the LLM's "connect two metrics" pattern) ----
    b.fees?.level === 'LOW' && (b.mempool?.level === 'LIGHT' || b.mempool?.level === 'NORMAL') && {
      key: 'conn', cat: 'mempool', score: 66,
      text: pick('conn-cheap', [
        'Low fees and a clear mempool. Cheap to transact and stack sats.',
        'Empty mempool, low fees. A good window to move on-chain.',
        'Fees low with room in the mempool. Cheap block space right now.',
      ]),
    },
    (b.fees?.level === 'HIGH' || b.fees?.level === 'EXTREME')
      && (b.mempool?.level === 'BUSY' || b.mempool?.level === 'CONGESTED') && {
      key: 'conn', cat: 'mempool', score: 82,
      text: pick('conn-busy', [
        `Fees ${feeRate(raw.fastestFee)} as the mempool backs up. Demand competes for block space.`,
        `Block space contested: fees ${feeRate(raw.fastestFee)} with the mempool full.`,
      ]),
    },
    b.blockTime?.level === 'FAST' && hrTrend.direction === 'BULLISH' && {
      key: 'conn', cat: 'network', score: 64,
      text: pick('conn-fast', [
        'Blocks running fast as hashrate climbs. Difficulty will adjust upward.',
        'Fast blocks and rising hashrate. Expect an upward difficulty retarget.',
      ]),
    },
    corr?.value != null && Math.abs(corr.value) > 0.5 && {
      key: 'conn', cat: 'network', score: 60,
      text: pick('conn-corr', [
        `Price and hashrate ${corr.value > 0 ? 'moving in step' : 'diverging'} across recent weeks.`,
        `Hashrate and price ${corr.value > 0 ? 'tracking together' : 'pulling apart'} of late.`,
      ]),
    },
    m?.mining?.feeSubsidyRatio != null && m?.mining?.halvingProgress != null && {
      key: 'conn', cat: 'mining', score: 58,
      text: pick('conn-rev', [
        `Fees are ${fmt.pct(m.mining.feeSubsidyRatio, 1)} of block revenue. The subsidy still leads.`,
        `The ${btcSubsidy(m.mining.currentReward)} BTC subsidy still dwarfs fees at ${fmt.pct(m.mining.feeSubsidyRatio, 1)} of revenue.`,
      ]),
    },
  ];

  // Merge catalog-derived facts (drawn from the SAME field set the LLM prompt uses,
  // via getAILocalFacts) so the local engine reaches data parity with the LLM. The
  // ranker keeps bespoke analytical candidates on top; facts fill the tail and, with
  // per-run jitter, rotate which surface each analysis run.
  const enrichedInsightPool = [...insightPool, ...getAILocalFacts(canonical)];
  const insights = rank(enrichedInsightPool, 15, 4);

  // ---- Watch (outlook): scored candidates, ranked, each ≤10 words ----
  const outlookPool = [
    daProg != null && {
      key: 'o-diff', cat: 'network',
      score: daProg >= 75 ? 60 : 30,
      text: pick('o-diff', [
        `Difficulty: ${fmt.pct(daProg)} to next adjustment.`,
        `Difficulty: ${fmt.pct(daProg)} into this epoch.`,
      ]),
    },
    {
      key: 'o-mempool', cat: 'mempool',
      score: b.mempool?.level === 'CONGESTED' ? 58 : b.mempool?.level === 'BUSY' ? 40 : 26,
      text: (b.mempool?.level === 'CONGESTED' || b.mempool?.level === 'BUSY')
        ? ((b.fees?.level === 'HIGH' || b.fees?.level === 'EXTREME')
            ? `Mempool: ${lc(b.mempool.level)}, elevated fees likely persist.`
            : `Mempool: ${lc(b.mempool.level)}, fee pressure could rise as backlog builds.`)
        : pick('o-mp-clear', [
            'Mempool: rising backlog would lift fees.',
            'Mempool: clear for now, watch for backlog.',
          ]),
    },
    {
      key: 'o-hashrate', cat: 'network', score: 32,
      text: pick('o-hr', [
        'Hashrate: sustained growth signals miner strength.',
        'Hashrate: steady growth underpins security.',
      ]),
    },
    m?.mining?.halvingProgress != null && {
      key: 'o-halving', cat: 'mining', score: 28,
      text: pick('o-halving', [
        `Halving: ${fmt.pct(m.mining.halvingProgress)} through Epoch ${m.mining.currentEpoch || 5}.`,
        `Halving: Epoch ${m.mining.currentEpoch || 5} ${fmt.pct(m.mining.halvingProgress)} complete.`,
      ]),
    },
    trend.direction && trend.direction !== 'UNKNOWN' && {
      key: 'o-price', cat: 'market',
      score: trend.strength === 'STRONG' ? 55 : 34,
      text: pick('o-price', [
        `Price: ${lc(trend.direction)} trend, watch volatility.`,
        `Price: ${lc(trend.direction)} bias, mind the swings.`,
      ]),
    },
    (volLevel === 'HIGH' || volLevel === 'ELEVATED') && {
      key: 'o-vol', cat: 'market',
      score: volLevel === 'HIGH' ? 56 : 38,
      text: pick('o-vol', [
        `Volatility: ${lc(volLevel)}, expect wider price swings.`,
        `Volatility: ${lc(volLevel)}, brace for bigger moves.`,
      ]),
    },
    {
      key: 'o-ln', cat: 'lightning', score: 30,
      text: pick('o-ln', [
        'Lightning: capacity growth tracks real adoption.',
        'Lightning: rising capacity signals real usage.',
      ]),
    },
    b.fees && {
      key: 'o-fees', cat: 'mining',
      score: (b.fees.level === 'HIGH' || b.fees.level === 'EXTREME') ? 50 : b.fees.level === 'LOW' ? 30 : 24,
      text: (b.fees.level === 'HIGH' || b.fees.level === 'EXTREME')
        ? pick('o-fee-hi', ['Fees: elevated, time non-urgent transactions carefully.', 'Fees: hot, delay non-urgent transactions.'])
        : b.fees.level === 'LOW'
          ? pick('o-fee-lo', ['Fees: low, good window for on-chain moves.', 'Fees: cheap, a solid window to transact.'])
          : pick('o-fee-nm', ['Fees: normal, healthy block space demand.', 'Fees: steady, balanced block space demand.']),
    },
  ];

  const outlook = rank(outlookPool, 10, 3);

  // ---- Alerts: anomalies first, then notable warnings + positive signals ----
  const alerts = [];
  (an.anomalies || []).slice(0, 2).forEach((a) => {
    if (!a?.message) return;
    alerts.push({ type: a.severity === 'CRITICAL' ? 'warning' : 'info', message: capWords(a.message, 15) });
  });
  if (wants('mempool') && b.mempool?.level === 'CONGESTED') {
    alerts.push({ type: 'warning', message: 'Mempool congested. Higher fees needed to confirm on time.' });
  }
  if (wants('mining') && (b.fees?.level === 'HIGH' || b.fees?.level === 'EXTREME')) {
    alerts.push({ type: 'warning', message: `Fees ${b.fees.level.toLowerCase()}. Time transactions carefully.` });
  }
  if (wants('network') && b.hashrate?.level === 'HIGH') {
    alerts.push({ type: 'success', message: pick('a-hr', ['Hashrate high. Network security is excellent.', 'Hashrate strong. The timechain is well defended.']) });
  }
  if (wants('mining') && b.fees?.level === 'LOW') {
    alerts.push({ type: 'success', message: pick('a-fee', ['Fees low. Cheap to transact and stack sats.', 'Fees cheap. A good time to move on-chain.']) });
  }

  return {
    type: 'structured',
    data: {
      headline,
      overallSentiment: sentiment.toLowerCase(),
      confidence: 1,
      marketScore,
      sections,
      alerts: alerts.slice(0, 3),
      insights,
      outlook,
      progressBars: [
        m?.progressBars?.halving && { label: 'Next Halving', progress: m.progressBars.halving.progress, detail: m.progressBars.halving.detail },
        m?.progressBars?.difficulty && { label: 'Difficulty Adj', progress: m.progressBars.difficulty.progress, detail: m.progressBars.difficulty.detail },
      ].filter(Boolean),
    },
    provider: 'Timechain Stats AI',
    model: 'Timechain Stats AI',
    usedFallback: false,
    strategy: 'local',
    analytics: an,
  };
};
